<template>
  <div>
    <!-- View toolbar — three-state segmented control (Delivery /
         Revenue / Opportunities) plus, when a calculator panel is
         active, its single contextual input (CPM for Revenue,
         Seconds per ad for Opportunities). Moved out of the search
         bar so the search bar stays focused on data filters, and
         inputs live next to the view they configure. -->
    <div class="mb-4 bg-white rounded-xl border border-gray-200/60 overflow-hidden">
      <div class="px-4 py-3 flex items-center gap-2 flex-wrap">
        <div class="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1 h-[36px]">
          <button
            v-for="opt in adsPanelOptions"
            :key="opt.id"
            type="button"
            @click="onAdsPanelPicked(opt.id)"
            :class="[
              'px-3.5 h-[28px] rounded-md text-[12px] font-medium transition-all duration-150 inline-flex items-center gap-1.5',
              adsPanel === opt.id
                ? 'bg-white text-[var(--color-primary)] shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            ]"
            :title="opt.title"
          >
            <span v-html="opt.icon"></span>
            {{ opt.name }}
          </button>
        </div>
      </div>

      <!-- Revenue calculator input — one CPM applied to whichever
           pipeline(s) the ad-type filter includes. Same shape as when
           it lived in AdsToolbar; only the position changed. -->
      <div
        v-if="adsPanel === 'revenue'"
        class="border-t border-gray-100 px-4 py-3 flex items-center gap-4 flex-wrap bg-gray-50/40"
      >
        <label class="flex items-center gap-2 text-[12px] text-gray-600">
          <span class="flex items-center gap-1.5 font-semibold text-gray-600 uppercase tracking-wider">
            <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="12" y1="1" x2="12" y2="23"/>
              <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>
            </svg>
            CPM ($/1000)
          </span>
          <div class="relative">
            <span class="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-[12px]">$</span>
            <input
              type="number"
              min="0"
              step="0.01"
              v-model.number="cpmInput"
              @input="onCpmInput"
              class="input-plain w-28 h-[32px] pl-5 pr-2 text-[12px] border border-gray-200 rounded-md focus:border-[var(--color-primary)] focus:outline-none"
              placeholder="0.00"
            />
          </div>
        </label>
        <p class="text-[11px] text-gray-400 max-w-[420px] leading-snug">
          Modeled revenue, not booked revenue. Real CPMs vary by geo, time
          of day, demographic, fill rate &mdash; treat the numbers as a
          what-if scenario.
        </p>
      </div>

      <!-- Opportunities calculator inputs — per-ad duration + CPM.
           Duration drives the expected-ad count (time-in-ads ÷ this);
           CPM turns that count into a potential-revenue dollar figure. -->
      <div
        v-if="adsPanel === 'opportunities'"
        class="border-t border-gray-100 px-4 py-3 flex items-center gap-4 flex-wrap bg-gray-50/40"
      >
        <label class="flex items-center gap-2 text-[12px] text-gray-600">
          <span class="flex items-center gap-1.5 font-semibold text-gray-600 uppercase tracking-wider">
            <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
            Seconds per ad
          </span>
          <input
            type="number"
            min="1"
            step="1"
            v-model.number="adDurationSecInput"
            @input="onAdDurationSecInput"
            class="input-plain w-24 h-[32px] px-2 text-[12px] border border-gray-200 rounded-md focus:border-[var(--color-primary)] focus:outline-none"
            placeholder="30"
          />
        </label>
        <label class="flex items-center gap-2 text-[12px] text-gray-600">
          <span class="flex items-center gap-1.5 font-semibold text-gray-600 uppercase tracking-wider">
            <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="12" y1="1" x2="12" y2="23"/>
              <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>
            </svg>
            CPM ($/1000)
          </span>
          <div class="relative">
            <span class="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-[12px]">$</span>
            <input
              type="number"
              min="0"
              step="0.01"
              v-model.number="cpmOppInput"
              @input="onOppCpmInput"
              class="input-plain w-28 h-[32px] pl-5 pr-2 text-[12px] border border-gray-200 rounded-md focus:border-[var(--color-primary)] focus:outline-none"
              placeholder="0.00"
            />
          </div>
        </label>
        <p class="text-[11px] text-gray-400 max-w-[420px] leading-snug">
          Time-in-ads &divide; Seconds-per-ad = expected ad slots. Multiply by
          CPM to model the potential revenue if every slot had been sold.
        </p>
      </div>
    </div>

    <!-- Default view (Off panel) — the standard ads dashboard: main
         KPI strip + five charts. When either Revenue or Opportunities
         is active, this whole block is hidden and only the active
         panel's cards + chart render. Keeps each mode focused on its
         own question instead of piling everything on one page. -->
    <template v-if="adsPanel === 'off'">
    <!-- KPI strip — six cards.
         Per-session row (the headline answer to "how long do users stay
         in ad breaks?"): Avg per session / Total ad time / Sessions
         with ads.
         Per-attempt row: Attempts / Completion rate / Skip rate. -->
    <div class="kpi-grid mb-6">
      <template v-if="loading">
        <div v-for="i in 6" :key="'skel-' + i" class="bg-white rounded-xl border border-gray-200/60 p-5">
          <LoadingBlock height="80" label="Loading stats..." />
        </div>
      </template>
      <template v-else-if="data">
        <div
          v-for="(card, i) in kpiCards"
          :key="card.key"
          :class="[
            'animate-fade-in relative',
            `stagger-${i + 1}`,
            activeInfoCard === card.key ? 'z-50' : ''
          ]"
        >
          <div class="bg-white rounded-xl border border-gray-200/60 p-5">
            <div class="flex items-center justify-between mb-3">
              <div class="flex items-center gap-1.5 min-w-0">
                <span class="text-sm font-medium text-gray-400 uppercase tracking-wider truncate">{{ card.label }}</span>
                <div class="relative shrink-0">
                  <button
                    @click.stop="toggleInfo(card.key)"
                    type="button"
                    class="w-4 h-4 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 flex items-center justify-center text-[10px] font-semibold leading-none transition-colors"
                    :title="cardInfo(card)"
                  >?</button>
                  <!-- Help dialog: long-form explanation of the metric.
                       Anchors right on the rightmost card so it grows
                       leftward and doesn't push horizontal scroll. -->
                  <div
                    v-if="activeInfoCard === card.key"
                    @click.stop
                    :class="[
                      'absolute top-full mt-2 z-50 w-64 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm text-gray-600 leading-relaxed',
                      i === kpiCards.length - 1 ? 'right-0' : 'left-0'
                    ]"
                  >{{ cardInfo(card) }}</div>
                </div>
              </div>
              <span :class="['flex items-center justify-center w-9 h-9 rounded-lg shrink-0', card.iconBg]" v-html="card.icon" />
            </div>
            <div class="flex items-baseline gap-2">
              <div class="font-data text-[26px] font-medium text-gray-900 leading-none">{{ card.value }}</div>
              <DeltaBadge v-if="card.current != null" :current="card.current" :previous="card.previous" :inverse="!!card.inverse" :formatter="card.format || formatNumber" />
            </div>
            <p class="text-sm text-gray-400 mt-2 truncate" :title="card.hint">{{ card.hint }}</p>
          </div>
        </div>
      </template>
    </div>
    </template> <!-- end main KPI strip (adsPanel === 'off') -->

    <!-- Revenue section — conditional on the toolbar's panel selector
         (Revenue tab). Computed entirely client-side from the ads block
         + CPMs; no server round-trip needed. Surfaces gross / earned
         revenue, per-session value, and an extrapolated monthly
         run-rate, plus a paired-line chart of gross + earned over time. -->
    <template v-if="adsPanel === 'revenue'">
      <div class="mb-3 flex items-center gap-2">
        <h2 class="text-[15px] font-semibold text-gray-900 flex items-center gap-1.5">
          <svg class="w-4 h-4 text-[var(--color-primary)]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="12" y1="1" x2="12" y2="23"/>
            <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>
          </svg>
          Revenue (modeled)
        </h2>
        <span class="text-[11px] text-gray-400">
          CPM ${{ (revenue.cpm || 0).toFixed(2) }}
        </span>
      </div>

      <!-- Revenue KPI strip (4 cards). -->
      <div class="kpi-grid mb-6">
        <template v-if="loading">
          <div v-for="i in 4" :key="'skel-rev-' + i" class="bg-white rounded-xl border border-gray-200/60 p-5">
            <LoadingBlock height="80" label="Loading stats..." />
          </div>
        </template>
        <div
          v-else
          v-for="(card, i) in revenueCards"
          :key="card.key"
          :class="[
            'animate-fade-in relative',
            `stagger-${i + 1}`,
            (activeInfoCard === card.key || hoveredInfoCard === card.key) ? 'z-50' : ''
          ]"
        >
          <div
            :class="[
              'rounded-xl border p-5',
              card.variant === 'reference'
                ? 'bg-gray-50/70 border-dashed border-gray-300'
                : 'bg-white border-gray-200/60'
            ]"
          >
            <div class="flex items-center justify-between mb-3">
              <div class="flex items-center gap-1.5 min-w-0">
                <span
                  :class="[
                    'text-sm font-medium uppercase tracking-wider truncate',
                    card.variant === 'reference' ? 'text-gray-500' : 'text-gray-400'
                  ]"
                >{{ card.label }}</span>
                <span
                  v-if="card.variant === 'reference'"
                  class="text-[9px] font-semibold text-gray-500 bg-gray-200/70 px-1.5 py-0.5 rounded uppercase tracking-wider shrink-0"
                >Ref</span>
                <div class="relative shrink-0">
                  <button
                    @click.stop="toggleInfo(card.key)"
                    type="button"
                    class="w-4 h-4 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 flex items-center justify-center text-[10px] font-semibold leading-none transition-colors"
                    :title="cardInfo(card)"
                  >?</button>
                  <div
                    v-if="activeInfoCard === card.key"
                    @click.stop
                    :class="[
                      'absolute top-full mt-2 z-[100] w-64 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm text-gray-600 leading-relaxed',
                      i === revenueCards.length - 1 ? 'right-0' : 'left-0'
                    ]"
                  >{{ cardInfo(card) }}</div>
                </div>
              </div>
              <span
                :class="['flex items-center justify-center w-9 h-9 rounded-lg shrink-0', card.iconBg]"
                v-html="card.icon"
              />
            </div>
            <div
              :class="[
                'font-data font-medium leading-none text-[26px]',
                card.variant === 'reference' ? 'text-gray-700' : 'text-gray-900'
              ]"
            >{{ card.value }}</div>
            <!-- Description with a hover-triggered info popover. Same
                 styled tooltip as the `?` button so the user can read
                 the formula / explanation without clicking. `?` still
                 toggles for touch devices where hover isn't available. -->
            <div
              class="relative mt-2"
              @mouseenter="hoveredInfoCard = card.key"
              @mouseleave="hoveredInfoCard = null"
            >
              <p class="text-sm text-gray-400 truncate cursor-help">{{ card.hint }}</p>
              <div
                v-if="hoveredInfoCard === card.key && activeInfoCard !== card.key"
                :class="[
                  'absolute top-full mt-2 z-[100] w-64 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm text-gray-600 leading-relaxed pointer-events-none',
                  i === revenueCards.length - 1 ? 'right-0' : 'left-0'
                ]"
              >{{ cardInfo(card) }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Revenue over time — ONE card per active pipeline, stacked
           in their own rows. Ad-type filter decides which cards
           render: "All" → both (Preroll on top, Midroll below),
           Preroll → only Preroll, Midroll → only Midroll. Each card
           plots two lines: Gross (impression-billed) + Earned
           (completion-billed). -->
      <div class="mb-6 space-y-6">
        <div
          v-for="p in revenuePipelineCards"
          :key="p.key"
          class="bg-white rounded-xl border border-gray-200/60"
        >
          <div class="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h2 class="text-[15px] font-semibold text-gray-900">Revenue over time &mdash; {{ p.label }}</h2>
              <p class="text-sm text-gray-400 mt-0.5">
                Gross revenue per bucket &mdash;
                {{ p.units }} {{ p.unitSource }} &times; ${{ (revenue?.cpm || 0).toFixed(2) }} CPM &divide; 1000
              </p>
            </div>
            <span class="text-sm font-data font-medium text-[var(--color-primary)] bg-[var(--color-primary-light)] px-3 py-1 rounded">
              {{ formatCurrency(p.gross) }} gross
            </span>
          </div>
          <div class="p-6">
            <LoadingBlock v-if="loading" height="280" label="Loading chart..." />
            <template v-else-if="hasRevenueTimeline">
              <div class="h-[280px] relative animate-fade-in">
                <canvas :ref="setRevenueCanvasRef(p.key)"></canvas>
              </div>
            </template>
            <template v-else><EmptyChart label="No revenue to plot for this period" /></template>
          </div>
        </div>
      </div>
    </template>

    <!-- Opportunities section — conditional on the toolbar's
         Opportunities tab. Frames the same ad activity as "slots we
         had", answering the question "did every ad break time get
         backfilled with an ad the SDK actually served?". -->
    <template v-if="adsPanel === 'opportunities'">
      <div class="mb-3 flex items-center gap-2">
        <h2 class="text-[15px] font-semibold text-gray-900 flex items-center gap-1.5">
          <svg class="w-4 h-4 text-[var(--color-primary)]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12 6 12 12 16 14"/>
          </svg>
          Ad opportunities
        </h2>
        <span class="text-[11px] text-gray-400">
          {{ opportunityDurationLabel }}
        </span>
      </div>

      <!-- Opportunity KPI strip. Same help-dialog pattern and layout
           as the Delivery + Revenue strips. -->
      <div class="kpi-grid mb-6">
        <template v-if="loading">
          <div v-for="i in 3" :key="'skel-opp-' + i" class="bg-white rounded-xl border border-gray-200/60 p-5">
            <LoadingBlock height="80" label="Loading stats..." />
          </div>
        </template>
        <div
          v-else
          v-for="(card, i) in opportunityCards"
          :key="card.key"
          :class="[
            'animate-fade-in relative',
            `stagger-${i + 1}`,
            activeInfoCard === card.key ? 'z-50' : ''
          ]"
        >
          <div class="bg-white rounded-xl border border-gray-200/60 p-5">
            <div class="flex items-center justify-between mb-3">
              <div class="flex items-center gap-1.5 min-w-0">
                <span class="text-sm font-medium text-gray-400 uppercase tracking-wider truncate">{{ card.label }}</span>
                <div class="relative shrink-0">
                  <button
                    @click.stop="toggleInfo(card.key)"
                    type="button"
                    class="w-4 h-4 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 flex items-center justify-center text-[10px] font-semibold leading-none transition-colors"
                    :title="cardInfo(card)"
                  >?</button>
                  <div
                    v-if="activeInfoCard === card.key"
                    @click.stop
                    :class="[
                      'absolute top-full mt-2 z-50 w-64 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm text-gray-600 leading-relaxed',
                      i === opportunityCards.length - 1 ? 'right-0' : 'left-0'
                    ]"
                  >{{ cardInfo(card) }}</div>
                </div>
              </div>
              <span :class="['flex items-center justify-center w-9 h-9 rounded-lg shrink-0', card.iconBg]" v-html="card.icon" />
            </div>
            <div class="font-data text-[26px] font-medium text-gray-900 leading-none">{{ card.value }}</div>
            <p class="text-sm text-gray-400 mt-2 truncate" :title="card.hint">{{ card.hint }}</p>
          </div>
        </div>
      </div>

      <!-- Opportunities over time — single-series line chart showing
           the estimated ad slots per bucket. Uses the same
           fillTimeGaps + formatChartLabel treatment as the other
           timelines so axes align across the section. -->
      <div class="mb-6 bg-white rounded-xl border border-gray-200/60">
        <div class="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 class="text-[15px] font-semibold text-gray-900">Opportunities over time</h2>
            <p class="text-sm text-gray-400 mt-0.5">Estimated ad slots per bucket</p>
          </div>
          <span class="text-sm font-data font-medium text-[var(--color-primary)] bg-[var(--color-primary-light)] px-3 py-1 rounded">
            {{ formatNumber(opportunityAdsExpected) }} total
          </span>
        </div>
        <div class="p-6">
          <LoadingBlock v-if="loading" height="280" label="Loading chart..." />
          <template v-else-if="hasOpportunityTimeline">
            <div class="h-[280px] relative animate-fade-in"><canvas ref="opportunityTimelineChart"></canvas></div>
          </template>
          <template v-else><EmptyChart label="No opportunities in this period" /></template>
        </div>
      </div>
    </template>

    <template v-if="adsPanel === 'off'"> <!-- default charts (Off panel) -->

    <!-- Avg ad time per session — over the date range. The headline
         chart of this view: each bucket carries the average wall-clock
         ms each session in that bucket spent in ads. -->
    <div class="mb-6 bg-white rounded-xl border border-gray-200/60">
      <div class="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
        <div>
          <h2 class="text-[15px] font-semibold text-gray-900">Time in ad breaks (per session)</h2>
          <p class="text-sm text-gray-400 mt-0.5">Average wall-clock each session spent in ads</p>
        </div>
        <span v-if="ads" class="text-sm font-data font-medium text-[var(--color-primary)] bg-[var(--color-primary-light)] px-3 py-1 rounded">
          {{ formatDurationMs(ads.avgAdMsPerSession) }} avg
        </span>
      </div>
      <div class="p-6">
        <LoadingBlock v-if="loading" height="280" label="Loading chart..." />
        <template v-else-if="hasSessionTimeline">
          <div class="h-[280px] relative animate-fade-in"><canvas ref="sessionTimelineChart"></canvas></div>
        </template>
        <template v-else><EmptyChart label="No ad sessions in this period" /></template>
      </div>
    </div>

    <!-- Per-attempt timeline — count of attempts per bucket. Sits
         right under the per-session timeline so the two time-series
         charts (volume vs duration) read as a pair. -->
    <div class="mb-6 bg-white rounded-xl border border-gray-200/60">
      <div class="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
        <div>
          <h2 class="text-[15px] font-semibold text-gray-900">Ad attempts over time</h2>
          <p class="text-sm text-gray-400 mt-0.5">Number of ads served per bucket</p>
        </div>
        <span v-if="ads" class="text-sm font-data font-medium text-[var(--color-primary)] bg-[var(--color-primary-light)] px-3 py-1 rounded">
          {{ formatNumber(ads.attempts) }} total
        </span>
      </div>
      <div class="p-6">
        <LoadingBlock v-if="loading" height="280" label="Loading chart..." />
        <template v-else-if="hasAttemptsTimeline">
          <div class="h-[280px] relative animate-fade-in"><canvas ref="attemptsTimelineChart"></canvas></div>
        </template>
        <template v-else><EmptyChart label="No attempts in this period" /></template>
      </div>
    </div>

    <!-- Distribution histogram — how many sessions watched how much ad
         time. Reveals long-tail: avg can hide a few heavy listeners. -->
    <div class="mb-6 bg-white rounded-xl border border-gray-200/60">
      <div class="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
        <div>
          <h2 class="text-[15px] font-semibold text-gray-900">Distribution of ad time per session</h2>
          <p class="text-sm text-gray-400 mt-0.5">How long sessions stay in ad breaks, bucketed</p>
        </div>
        <span v-if="ads" class="text-sm font-data font-medium text-[var(--color-primary)] bg-[var(--color-primary-light)] px-3 py-1 rounded">
          {{ formatNumber(ads.sessionsWithAds) }} sessions
        </span>
      </div>
      <div class="p-6">
        <LoadingBlock v-if="loading" height="260" label="Loading chart..." />
        <template v-else-if="hasHistogram">
          <div class="h-[260px] relative animate-fade-in"><canvas ref="histogramChart"></canvas></div>
        </template>
        <template v-else><EmptyChart label="No data for histogram" /></template>
      </div>
    </div>

    <!-- Per-attempt charts: outcome breakdown + pipeline split. -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
      <div class="bg-white rounded-xl border border-gray-200/60">
        <div class="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 class="text-[15px] font-semibold text-gray-900">Outcome breakdown</h2>
            <p class="text-sm text-gray-400 mt-0.5">How ad attempts ended</p>
          </div>
          <span v-if="ads" class="text-sm font-data font-medium text-[var(--color-primary)] bg-[var(--color-primary-light)] px-3 py-1 rounded">
            {{ formatNumber(ads.attempts) }} attempts
          </span>
        </div>
        <div class="p-6">
          <LoadingBlock v-if="loading" height="260" label="Loading chart..." />
          <template v-else-if="hasOutcome">
            <div class="h-[260px] relative animate-fade-in"><canvas ref="outcomeChart"></canvas></div>
          </template>
          <template v-else><EmptyChart label="No ad attempts" /></template>
        </div>
      </div>

      <div class="bg-white rounded-xl border border-gray-200/60">
        <div class="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 class="text-[15px] font-semibold text-gray-900">Pipeline split</h2>
            <p class="text-sm text-gray-400 mt-0.5">IMA preroll vs SSAI midrolls</p>
          </div>
        </div>
        <div class="p-6">
          <LoadingBlock v-if="loading" height="260" label="Loading chart..." />
          <template v-else-if="hasPipeline">
            <div class="h-[260px] relative animate-fade-in"><canvas ref="pipelineChart"></canvas></div>
          </template>
          <template v-else><EmptyChart label="No ad attempts" /></template>
        </div>
      </div>
    </div>
    </template> <!-- end default charts (adsPanel === 'off') -->

  </div>
</template>

<script>
import Chart from 'chart.js/auto'
import { mapGetters } from 'vuex'
import api from '../../services/api.js'
import LoadingBlock from '../../components/LoadingBlock.vue'
import { notifyError } from '../../services/notifications.js'
import DeltaBadge from './components/DeltaBadge.vue'

// Same palette as overview / lives / DonutCard so every donut across
// the dashboard reads with the same color schema. Order is fixed —
// slices are colored positionally (1st slice = #3393ff, 2nd = #10b981,
// etc.) regardless of semantic meaning, matching the convention used
// by every other doughnut on the dashboard.
const COLORS = {
  bar: 'rgba(51, 147, 255, 0.65)',
  line: '#3393ff',
  lineFill: 'rgba(51, 147, 255, 0.08)',
  doughnut: ['#3393ff', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#6b7280', '#ec4899', '#14b8a6'],
  grid: '#f5f5f5',
  tick: '#a3a3a3'
}
const CHART_SCALES = {
  x: { grid: { display: false }, ticks: { font: { family: 'Poppins', size: 11 }, color: COLORS.tick } },
  y: { grid: { color: COLORS.grid }, ticks: { font: { family: 'JetBrains Mono', size: 11 }, color: COLORS.tick } }
}

const EmptyChart = {
  props: { label: String },
  template: `
    <div class="flex flex-col items-center justify-center py-12 text-center">
      <div class="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mb-3">
        <svg class="w-5 h-5 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
        </svg>
      </div>
      <p class="text-base text-gray-400">{{ label }}</p>
    </div>
  `
}

/**
 * Analytics Ads — focused view on ad-break engagement. Powered by the
 * `ads` block returned by the same /analytics/{overview,lives,medias}
 * endpoints the other tabs hit; route selection is driven by the
 * AdsToolbar's targetType + liveId/mediaId filters.
 *
 * Headline metric: `avgAdMsPerSession` — average wall-clock each
 * session spent in ad breaks. Surfaced as the first KPI card and the
 * top chart. Supporting metrics fill out the per-attempt picture
 * (counts, outcome rates, pipeline split).
 */
export default {
  name: 'AnalyticsAds',
  components: { EmptyChart, LoadingBlock, DeltaBadge },

  data() {
    return {
      loading: false,
      data: null,
      charts: {},
      // Which card's help dialog is currently open. Single value
      // shared across BOTH KPI strips (ads + revenue) since the keys
      // are unique across the two sets — opening one dismisses any
      // other that was open, which matches user expectation.
      activeInfoCard: null,
      // Which card's description is currently under the cursor.
      // Drives the hover-triggered info popover on the Revenue KPI
      // cards. Kept separate from `activeInfoCard` (the click-toggled
      // one) so mouseleave doesn't dismiss a popover a touch user
      // deliberately opened via the `?` button.
      hoveredInfoCard: null,
      // Calculator inputs — locally controlled, mirrored into the
      // store on each keystroke. Seeded from the store in mounted()
      // so localStorage-persisted values carry across reloads. Not
      // bound directly to the store to sidestep mid-typing NaN
      // states colliding with Vuex strict mode.
      cpmInput: 0,
      adDurationSecInput: 30,
      cpmOppInput: 0
    }
  },

  computed: {
    ...mapGetters('analytics', ['applied', 'revenue', 'opportunities', 'adsPanel']),

    /** Shortcut into the response's ads block. */
    ads() { return this.data?.ads || null },

    /** Segmented-control options for the panel selector rendered at
     * the top of this view. Moved out of AdsToolbar so the search
     * bar stays focused on data filters. */
    adsPanelOptions() {
      return [
        // The 'off' id is preserved for backwards-compat with the
        // store + localStorage — only the label changes. "Delivery"
        // reads naturally for the default view (ad delivery metrics:
        // attempts, completion, skip, outcome, pipeline, timelines).
        { id: 'off',           name: 'Delivery',      title: 'Ad delivery metrics — attempts, completion, skip, outcome',
          icon: '<svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="9" rx="1"/><rect x="14" y="3" width="7" height="5" rx="1"/><rect x="14" y="12" width="7" height="9" rx="1"/><rect x="3" y="16" width="7" height="5" rx="1"/></svg>' },
        { id: 'opportunities', name: 'Opportunities', title: 'Ad slots we had, filled, and expected',
          icon: '<svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>' },
        { id: 'revenue',       name: 'Revenue',       title: 'Model revenue at a given CPM',
          icon: '<svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>' }
      ]
    },

    // ---- Revenue calculator (client-side, no API call) ----

    /**
     * Per-pipeline impression counts used for revenue math. Prefer
     * the server's VAST-accurate `ads.impressions.*` (counts actual
     * ad_impression / ssai_ad_impression events that the SDK emitted
     * per VAST spec). Fall back to raw `byPipeline.*` attempt counts
     * when the impressions block reports zero — this catches the very
     * common case where the SDK fires ad attempts but never emits the
     * matching impression event (early rollout, older SDK build, a
     * pipeline that hasn't wired impression tracking yet). Without
     * this fallback, entering a CPM produces $0 revenue even though
     * "Ad attempts over time" clearly shows traffic, and there is no
     * signal to the user why. `||` (not `??`) so that a zero — not
     * just an undefined — triggers the fallback, since the server
     * always populates the impressions block with defaults.
     */
    impressionsIma()  { return this.ads?.impressions?.ima  || this.ads?.byPipeline?.ima  || 0 },
    impressionsSsai() { return this.ads?.impressions?.ssai || this.ads?.byPipeline?.ssai || 0 },

    /** Per-pipeline gross revenue — impressions × single CPM ÷ 1000.
     * The toolbar's ad-type filter narrows the underlying data
     * server-side, so a pipeline the user excluded already returns 0
     * impressions from the API; no extra client-side gating needed. */
    grossRevenueIma()  { return this._impressionsToRevenue(this.impressionsIma,  this.revenue?.cpm) },
    grossRevenueSsai() { return this._impressionsToRevenue(this.impressionsSsai, this.revenue?.cpm) },
    grossRevenue()     { return this.grossRevenueIma + this.grossRevenueSsai },

    /** Which pipelines the ad-type filter includes right now — drives
     * the per-pipeline card grid and its render pass. `adType` is null
     * for "All" (both pipelines). */
    activePipelines() {
      const t = this.applied?.adType
      if (t === 'ima')  return ['ima']
      if (t === 'ssai') return ['ssai']
      return ['ima', 'ssai']
    },

    /** One entry per active pipeline — fed to the template's v-for
     * that renders one chart card per pipeline. Each entry carries
     * everything the card needs: label, per-pipeline gross total,
     * unit count + source (impressions or attempts) for the subtitle. */
    revenuePipelineCards() {
      return this.activePipelines.map(key => {
        const label = key === 'ima' ? 'Preroll' : 'Midroll'
        const imp = key === 'ima' ? (this.ads?.impressions?.ima || 0) : (this.ads?.impressions?.ssai || 0)
        const att = key === 'ima' ? (this.ads?.byPipeline?.ima  || 0) : (this.ads?.byPipeline?.ssai  || 0)
        const units = imp || att
        const unitSource = imp > 0 ? 'impressions' : (att > 0 ? 'attempts' : 'impressions')
        const gross = key === 'ima' ? this.grossRevenueIma : this.grossRevenueSsai
        return { key, label, units: this.formatNumber(units), unitSource, gross }
      })
    },

    /**
     * Earned revenue — completion-billed model. With VAST-accurate
     * impression counts available from the server, the model
     * collapses to `gross × completionRate`: of the impressions that
     * fired, the completion rate is the share that also reached the
     * Complete event. Falls back gracefully when completionRate is
     * null (no terminal events to compute it from).
     */
    earnedRevenue() {
      const rate = this.ads?.completionRate
      if (rate == null) return this.grossRevenue   // no data → assume earned = gross
      return this.grossRevenue * rate
    },

    /**
     * Revenue per session — average earnings squeezed out of a
     * single session that saw any ad. Divides GROSS (not Earned)
     * because the Earned card is on hold and referencing a hidden
     * metric in the hint is confusing. When Earned comes back,
     * flip this to `this.earnedRevenue / sessions` — the two are
     * within one completion-rate multiplier.
     */
    revenuePerSession() {
      const sessions = this.ads?.sessionsWithAds || 0
      if (!sessions) return 0
      return this.grossRevenue / sessions
    },

    /**
     * Monthly run-rate — simple per-day extrapolation. Computes
     * avg(gross/day) over the active window and multiplies by 30.
     * Naive but useful as a rough projection — UI is honest about it.
     */
    monthlyRunRate() {
      const from = this.applied.dateFrom ? new Date(this.applied.dateFrom).getTime() : 0
      const to   = this.applied.dateTo   ? new Date(this.applied.dateTo).getTime()   : 0
      const days = (to - from) / (24 * 3600 * 1000)
      if (!days || days <= 0) return 0
      const perDay = this.grossRevenue / days
      return perDay * 30
    },

    /**
     * Per-bucket revenue series, split by pipeline and by billing
     * model. Server doesn't pre-aggregate impressions per pipeline
     * per bucket (would need extra $cond fields on the timeline
     * $group), so we distribute each pipeline's period-level
     * impression count across buckets in proportion to that bucket's
     * share of total attempts. Each bucket carries four flat values
     * so `fillTimeGaps` (which takes a string key) can pick them:
     *   imaGross, imaEarned, ssaiGross, ssaiEarned
     * Earned = gross × completionRate (falls back to gross when the
     * server reports null completionRate — no terminal events yet).
     */
    revenueTimelineSeries() {
      const timeline = this.ads?.timeline || []
      const totalAttempts = this.ads?.attempts || 0
      const cpm = this.revenue?.cpm || 0
      const completionRate = this.ads?.completionRate ?? 1
      const imaImpressions  = this.impressionsIma
      const ssaiImpressions = this.impressionsSsai
      return timeline.map(b => {
        const share = totalAttempts > 0 ? (b.attempts || 0) / totalAttempts : 0
        const imaGross  = ((imaImpressions  * share) / 1000) * cpm
        const ssaiGross = ((ssaiImpressions * share) / 1000) * cpm
        return {
          _id: b._id,
          imaGross,
          imaEarned:  imaGross  * completionRate,
          ssaiGross,
          ssaiEarned: ssaiGross * completionRate
        }
      })
    },

    hasRevenueTimeline() {
      return this.revenueTimelineSeries.length > 0
    },

    /** Revenue KPI cards. The reference card leads so the base
     * count is visible before the outcome metrics that use it. */
    revenueCards() {
      return [
        {
          key: 'adAttempts', label: 'Ad attempts',
          variant: 'reference',
          value: this.formatNumber(this.ads?.attempts || 0),
          hint: 'Reference count for Gross revenue',
          info: 'How many individual ads the player tried to serve in this time range. Each ad in a break counts once — a break with 3 ads adds 3 to the total. Used as a fallback base for the Gross revenue estimate when the player can\'t report the more precise "impression fired" signal. Pick Preroll or Midroll above to narrow to one type of ad.',
          icon: '<svg class="w-4 h-4 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>',
          iconBg: 'bg-gray-200/70'
        },
        {
          key: 'gross', label: 'Gross revenue',
          value: this.formatCurrency(this.grossRevenue),
          // Reveal the source count so the user can see what's driving
          // the number — falls back to attempts when the SDK didn't
          // emit matching impression events (per-pipeline, so IMA can
          // use impressions while SSAI uses attempts, or vice versa).
          hint: this._revenueSourceHint(),
          info: 'A modeled estimate of what your ads would have made at the CPM you entered. CPM is the industry rate for a thousand ad views, so the math is (ads served × your CPM) ÷ 1000. For each ad type (Preroll and Midroll), we prefer the more accurate "impression fired" count when the player reports it, and fall back to the number of ad attempts (shown in the reference card) when it doesn\'t. This is a what-if number — real revenue depends on your booked deals, geography, time of day, and fill rate.',
          icon: '<svg class="w-4 h-4 text-blue-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>',
          iconBg: 'bg-blue-50'
        },
        // Earned revenue card — on hold. The underlying computed
        // `earnedRevenue` stays wired so Revenue/session still has its
        // input, and `imaEarned` / `ssaiEarned` remain in the series
        // shape so the chart can restore its Earned line by adding
        // back the dataset in renderRevenueTimeline(). To re-enable:
        // uncomment this block.
        // {
        //   key: 'earned', label: 'Earned revenue',
        //   value: this.formatCurrency(this.earnedRevenue),
        //   hint: 'Completed ads only (completion-billed model)',
        //   info: 'Revenue from completed impressions only — the completion-billed deal number. Formula: gross × completionRate. Of the impressions that fired, the period-level completion rate is the share that also reached the COMPLETE event; multiplying gross by that rate gives earned. Falls back to gross when no terminal events exist (completionRate is null).',
        //   icon: '<svg class="w-4 h-4 text-green-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="9 12 11 14 16 9"/></svg>',
        //   iconBg: 'bg-green-50'
        // },
        {
          key: 'perSession', label: 'Revenue / session',
          value: this.formatCurrency(this.revenuePerSession, 3),
          hint: 'Gross ÷ Sessions with ads',
          info: 'How much a single listening session is worth on average — Gross revenue divided by the number of sessions where the listener saw at least one ad. Useful when comparing app to app, Preroll vs Midroll, or morning vs evening audiences. Shown to three decimals because a single session usually earns fractions of a cent.',
          icon: '<svg class="w-4 h-4 text-violet-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="18" x2="12" y2="21"/></svg>',
          iconBg: 'bg-violet-50'
        },
        {
          key: 'runRate', label: 'Monthly run-rate',
          value: this.formatCurrency(this.monthlyRunRate),
          hint: 'Rough monthly projection at the current daily pace',
          info: 'A rough projection of monthly revenue if the current daily pace kept up: (Gross ÷ number of days in the window) × 30. It doesn\'t account for weekends, seasonality, campaign changes, or growth trends — it\'s a directional number, not a forecast. Useful as a "roughly how big could this be per month" gut check.',
          icon: '<svg class="w-4 h-4 text-amber-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>',
          iconBg: 'bg-amber-50'
        }
      ]
    },

    // ---- Opportunities calculator (client-side) ----

    /**
     * Per-ad duration used for the "expected ads" math. Always sourced
     * from the toolbar's "Seconds per ad" field — the server-reported
     * mean is unreliable for this calculation because a single
     * analytics_ad_attempts row can span an entire SSAI pod (multiple
     * back-to-back spots), skewing the average well above the standard
     * spot length. The toolbar value is a stable, user-controlled
     * assumption; 30s is the industry-default fallback.
     */
    effectiveAdDurationMs() {
      const sec = this.opportunities?.adDurationSec
      const perAdSec = Number.isFinite(sec) && sec > 0 ? sec : 30
      return perAdSec * 1000
    },

    /** Human-readable subtitle for the panel header. */
    opportunityDurationLabel() {
      const s = this.opportunities?.adDurationSec || 30
      return `Using ${s}s per ad (toolbar setting)`
    },

    /**
     * Whole ads that actually fit inside the observed ad-break time,
     * given the per-ad duration. Uses Math.floor (not Math.round) so
     * "N opportunities × per-ad-duration ≤ total ad time" always
     * holds — a user reversing the math never sees a slot count that
     * exceeds the time available. Any leftover milliseconds are
     * "not enough for one more full ad" and don't count as an
     * opportunity. Represents "how many complete slots the ad-break
     * time could have contained".
     */
    opportunityAdsExpected() {
      const totalMs = this.ads?.totalAdMs || 0
      const perAdMs = this.effectiveAdDurationMs
      if (!totalMs || !perAdMs) return 0
      return Math.floor(totalMs / perAdMs)
    },

    /**
     * Ads that actually ran. Attempts is the natural count: every ad
     * the SDK started, regardless of outcome. Not impressions —
     * impressions filter out ads that started but never crossed the
     * VAST viewability threshold, which understates the "did we ATTEMPT
     * to serve an ad" cohort we want here.
     */
    opportunityAdsServed() {
      return this.ads?.attempts || 0
    },

    /**
     * Fill rate as a ratio: served ÷ expected. Above 1 means ads ran
     * shorter than the assumed duration (more slots filled in the
     * same time); below 1 means ads ran longer or the ad break time
     * exceeds actual ads served. Null when expected === 0.
     */
    opportunityFillRate() {
      const expected = this.opportunityAdsExpected
      if (!expected) return null
      return this.opportunityAdsServed / expected
    },

    /** Three opportunity KPI cards — Time in ad breaks + Ad opportunities +
     * Potential revenue. */
    opportunityCards() {
      const expected = this.opportunityAdsExpected
      const totalMs  = this.ads?.totalAdMs || 0
      const perAdMs  = this.effectiveAdDurationMs
      const perAdSec = perAdMs / 1000
      const cpm      = Number(this.opportunities?.cpm) || 0
      const potential = (expected * cpm) / 1000
      return [
        {
          key: 'oppTimeInAds', label: 'Time in ad breaks',
          value: this.formatDurationMs(totalMs),
          hint: 'Total time listeners spent watching ads',
          info: 'Total time your listeners spent watching or listening to ads across every session in this window. Only counts ads that finished (played through, were skipped, or errored) — ads still in progress at the moment you loaded the page aren\'t counted yet. This is the raw material for the Ad opportunities calculation below.',
          icon: '<svg class="w-4 h-4 text-violet-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>',
          iconBg: 'bg-violet-50'
        },
        {
          key: 'oppExpected', label: 'Ad opportunities',
          value: this.formatNumber(expected),
          hint: `Time in ads ÷ ${perAdSec.toFixed(1)}s per ad`,
          info: `How many ad slots could theoretically have fit into the total ad-break time — Time in ad breaks ÷ the "Seconds per ad" you set above (currently ${perAdSec.toFixed(1)}s per ad). Answers "if every second we spent showing ads had been perfectly filled with ${perAdSec.toFixed(1)}-second spots, how many ads would that be?" Use it to compare what we actually served vs. what the inventory could support.`,
          icon: '<svg class="w-4 h-4 text-blue-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>',
          iconBg: 'bg-blue-50'
        },
        {
          key: 'oppPotentialRevenue', label: 'Potential revenue',
          value: this.formatCurrency(potential),
          hint: `${this.formatNumber(expected)} slots × $${cpm.toFixed(2)} CPM ÷ 1000`,
          info: `The upside if every ad slot we could have filled had been sold at the CPM you entered above ($${cpm.toFixed(2)}). Formula: Ad opportunities × CPM ÷ 1000. Different question than the Revenue panel: Revenue models what we probably earned from ads that actually ran; this models what we could earn if we filled the whole ad-break time.`,
          icon: '<svg class="w-4 h-4 text-emerald-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>',
          iconBg: 'bg-emerald-50'
        }
      ]
    },

    /**
     * Per-bucket opportunities series. Two aligned values per bucket:
     *   - opportunities: totalDurationMs ÷ per-ad duration, floored
     *   - timeSpentSec:  totalDurationMs converted to whole seconds
     * Floor matches opportunityAdsExpected — a bucket's opportunity
     * count is the number of complete slots that fit, not a rounded
     * approximation, so reversing the math never lies about slots we
     * "should have had". Zero-buckets pass through unchanged so
     * fillTimeGaps can carry the x-labels.
     */
    opportunityTimelineSeries() {
      const timeline = this.ads?.timeline || []
      const perAdMs  = this.effectiveAdDurationMs
      if (!perAdMs) return []
      return timeline.map(b => {
        const totalMs = b.totalDurationMs || 0
        return {
          _id: b._id,
          opportunities: Math.floor(totalMs / perAdMs),
          timeSpentSec:  Math.round(totalMs / 1000)
        }
      })
    },

    hasOpportunityTimeline() {
      return this.opportunityTimelineSeries.length > 0
    },

    /**
     * KPI strip. Six cards split into two semantic rows:
     *   Per-session: Avg per session / Total ad time / Sessions
     *   Per-attempt: Attempts / Completion rate / Skip rate
     * The grid wraps 1 → 2 → 3 → 6 the same way other tabs do.
     */
    kpiCards() {
      const a = this.ads || {}
      return [
        {
          key: 'avgPerSession', label: 'Avg per session',
          value: this.formatDurationMs(a.avgAdMsPerSession),
          current: a.avgAdMsPerSession || 0, previous: a.avgAdMsPerSessionPrev,
          format: this.formatDurationMs,
          hint: 'Time the typical listener stays in ads',
          info: 'How much of an average session was spent in ad breaks. We add up all the ad time in each session, then average across every session that saw at least one ad. Only counts ads that finished (played through, were skipped, or errored). Best one-number answer to "how long does the typical listener sit through ads?"',
          icon: '<svg class="w-4 h-4 text-violet-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>',
          iconBg: 'bg-violet-50'
        },
        {
          key: 'totalAdTime', label: 'Total ad time',
          value: this.formatDurationMs(a.totalAdMs),
          current: a.totalAdMs || 0, previous: a.totalAdMsPrev,
          format: this.formatDurationMs,
          hint: 'Total ad time across all listeners',
          info: 'Total time your platform served ads across every listener session in this window. Equal to Avg per session multiplied by Sessions with ads. Useful for reconciling with your ad server\'s billing reports or estimating how much listening was actually ads vs content.',
          icon: '<svg class="w-4 h-4 text-blue-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/><path d="M12 8v4l3 3"/></svg>',
          iconBg: 'bg-blue-50'
        },
        {
          key: 'sessionsWithAds', label: 'Sessions with ads',
          value: this.formatNumber(a.sessionsWithAds),
          current: a.sessionsWithAds || 0, previous: a.sessionsWithAdsPrev,
          hint: 'Distinct sessions that saw at least one ad',
          info: 'How many separate listener sessions had at least one ad play (or try to play). This is the audience size for every other card in this row — the "Avg per session" number means very different things over 50 sessions vs. 50,000.',
          icon: '<svg class="w-4 h-4 text-rose-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="18" x2="12" y2="21"/></svg>',
          iconBg: 'bg-rose-50'
        },
        {
          key: 'adsDelivered', label: 'Ads Delivered',
          value: this.formatNumber(this._adsDelivered()),
          current: this._adsDelivered(),
          // Prev-period delivered — same per-pipeline fallback as
          // current so the delta is apples-to-apples.
          previous: this._adsDeliveredPrev(),
          hint: this._adsDeliveredHint(),
          info: 'Ads that actually reached the listener — the "billable" number your ad partners care about. We prefer the player\'s confirmed "impression fired" signal, which is the industry-standard delivery marker. If the player doesn\'t emit that signal (older SDK, ad-blockers), we fall back to counting ads that started playing — a skipped ad still counts as delivered because it rendered to the user. The hint above shows which source was used per ad type.',
          icon: '<svg class="w-4 h-4 text-indigo-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>',
          iconBg: 'bg-indigo-50'
        },
        {
          key: 'completionRate', label: 'Completion rate',
          value: this.formatPercent(a.completionRate),
          current: a.completionRate, previous: a.completedPrev != null && a.attemptsPrev > 0
            ? a.completedPrev / a.attemptsPrev
            : null,
          format: this.formatPercent,
          hint: 'Ads watched through to the end',
          info: 'What percentage of ads played all the way through: completed ÷ total attempts. A higher number is good — it means listeners aren\'t bailing on the ad break. A dropping rate usually points at overly-long ads or ad content that\'s not resonating.',
          icon: '<svg class="w-4 h-4 text-green-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="9 12 11 14 16 9"/></svg>',
          iconBg: 'bg-green-50'
        },
        {
          key: 'skipRate', label: 'Skip rate',
          value: this.formatPercent(a.skipRate),
          // Skip rate is "bad" when it rises — DeltaBadge inverse:true
          // colors a positive delta red.
          inverse: true,
          current: a.skipRate, previous: a.skippedPrev != null && a.completedPrev != null && (a.skippedPrev + a.completedPrev) > 0
            ? a.skippedPrev / (a.skippedPrev + a.completedPrev)
            : null,
          format: this.formatPercent,
          hint: 'Share of ads the listener chose to skip',
          info: 'Of the ads the listener actively decided to either watch or skip, what percentage were skipped: skipped ÷ (completed + skipped). Excludes ads that errored, ended abruptly, or are still playing — those weren\'t a real listener choice. Lower is better; the delta chip flips to red when this goes up.',
          icon: '<svg class="w-4 h-4 text-amber-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="5 4 15 12 5 20 5 4"/><line x1="19" y1="5" x2="19" y2="19"/></svg>',
          iconBg: 'bg-amber-50'
        }
      ]
    },

    hasSessionTimeline()  { return Array.isArray(this.ads?.sessionTimeline) && this.ads.sessionTimeline.length > 0 },
    hasAttemptsTimeline() { return Array.isArray(this.ads?.timeline) && this.ads.timeline.length > 0 },
    hasHistogram() {
      const h = this.ads?.histogram
      return Array.isArray(h) && h.some(b => b.sessions > 0)
    },
    hasOutcome()  { return this.ads && this.ads.attempts > 0 },
    hasPipeline() {
      const p = this.ads?.byPipeline
      return p && (p.ima > 0 || p.ssai > 0)
    }
  },

  watch: {
    applied: { handler() { this.loadAds() }, deep: true },
    /**
     * Redraw the revenue chart on CPM changes — no API call needed
     * since the math is client-side. nextTick gives Vue a frame to
     * mount/unmount the canvas before we try to draw on it.
     */
    revenue: {
      handler() { this.$nextTick(() => { if (this.adsPanel === 'revenue') this.renderRevenueTimeline() }) },
      deep: true
    },

    /**
     * Same-frame redraw when the user swaps panels — each mode's
     * canvases only exist while that mode is active, so re-mounting
     * requires an explicit render pass through renderAllCharts.
     */
    adsPanel() {
      this.$nextTick(() => this.renderAllCharts())
    },

    /**
     * Recompute the opportunities chart when the fallback per-ad
     * duration changes — server data hasn't changed, but the derived
     * opportunities-per-bucket has.
     */
    opportunities: {
      handler() { this.$nextTick(() => { if (this.adsPanel === 'opportunities') this.renderOpportunityTimeline() }) },
      deep: true
    }
  },

  mounted() {
    // AbortController for the in-flight /analytics/ads request. Aborted
    // on unmount / route-away and re-created inside loadAds() so a nav
    // or filter change cancels the previous run's HTTP work.
    this._abort = new AbortController()
    // Seed calculator inputs from the store (which itself reads
    // localStorage on module load), so the last-used values persist
    // across reloads and tab-switches.
    this.cpmInput           = this.revenue?.cpm || 0
    this.adDurationSecInput = this.opportunities?.adDurationSec || 30
    this.cpmOppInput        = this.opportunities?.cpm || 0
    this.loadAds()
    // Click anywhere outside an open help popover dismisses it. The
    // popover itself stops propagation on its own click, and the `?`
    // button toggles, so this only fires for true "outside" clicks.
    this._onDocClick = () => { this.activeInfoCard = null }
    document.addEventListener('click', this._onDocClick)
  },

  beforeUnmount() {
    // Abort but keep the controller — same reason as the other analytics
    // views: any callback queued behind the current run reads
    // `this._abort.signal`; keeping it truthy-and-aborted causes the
    // outgoing `api.get` to reject immediately.
    if (this._abort) this._abort.abort()
    Object.values(this.charts).forEach(c => { if (c) c.destroy() })
    if (this._onDocClick) document.removeEventListener('click', this._onDocClick)
  },

  methods: {
    /**
     * Toggle the help-dialog popover for a card key. Clicking the same
     * card closes it; clicking another swaps; clicking outside dismisses
     * via the document-level listener installed in mounted().
     */
    toggleInfo(key) {
      this.activeInfoCard = this.activeInfoCard === key ? null : key
    },

    /**
     * Help-dialog body. Prefers an explicit `info` field (long-form
     * explanation) when present; falls back to the card's short `hint`
     * so the dialog always has at least the truncated value visible.
     */
    cardInfo(card) {
      return card.info || card.hint
    },

    /**
     * Pick the API route based on the toolbar's targetType + entity
     * selection. Org-wide rolls up under the relevant collection's
     * cross-section endpoint (/analytics/lives or /analytics/medias);
     * a specific entity hits the per-id endpoint.
     */
    apiRoute() {
      // The ads tab hits the dedicated /ads/* endpoints instead of the
      // general per-target endpoints — the server-side handler runs
      // only ad-related aggregations (skipping rollups / geo / session
      // work), so the tab renders faster without pulling data it
      // never plots. Response shape mirrors the ads-relevant slice of
      // the general payload: { ads, viewers: { granularity } }.
      const t = this.applied.targetType || 'live'
      if (t === 'media') {
        return this.applied.mediaId ? `/analytics/ads/medias/${this.applied.mediaId}` : '/analytics/ads/medias'
      }
      return this.applied.liveId ? `/analytics/ads/lives/${this.applied.liveId}` : '/analytics/ads/lives'
    },

    async loadAds() {
      // Abort any in-flight request from the previous run, then start
      // fresh. Guarantees a nav-away or filter change cancels the
      // outstanding HTTP work instead of letting it complete unused.
      if (this._abort) this._abort.abort()
      this._abort = new AbortController()
      const signal = this._abort.signal
      try {
        this.loading = true
        this.$emit('loading', true)
        const params = {}
        if (this.applied.dateFrom) params.dateFrom = new Date(this.applied.dateFrom).toISOString()
        if (this.applied.dateTo)   params.dateTo   = new Date(this.applied.dateTo).toISOString()
        params.granularity = this.granularityFor(params.dateFrom, params.dateTo, this.applied.presetLabel)
        if (this.applied.adType)    params.pipeline = this.applied.adType
        if (this.applied.appName)   params.appName  = this.applied.appName
        if (this.applied.showId)    params.showId   = this.applied.showId
        const res = await api.get(this.apiRoute(), { params, silent: true, signal })
        this.data = res.data
      } catch (err) {
        // AbortController.abort() — expected on nav / filter change.
        if (err?.name === 'CanceledError' || err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return
        notifyError(err?.message || 'Failed to load ads analytics')
      } finally {
        this.loading = false
        this.$emit('loading', false)
        this.$nextTick(() => this.renderAllCharts())
      }
    },

    renderAllCharts() {
      // Only render the charts that are actually mounted this frame.
      // The three modes each show a distinct set of canvases; drawing
      // into a canvas that isn't in the DOM is silently discarded, but
      // it's wasted work and warms unnecessary Chart.js instances.
      if (this.adsPanel === 'off') {
        this.renderSessionTimeline()
        this.renderHistogram()
        this.renderOutcome()
        this.renderPipeline()
        this.renderAttemptsTimeline()
      } else if (this.adsPanel === 'revenue') {
        this.renderRevenueTimeline()
      } else if (this.adsPanel === 'opportunities') {
        this.renderOpportunityTimeline()
      }
    },

    /**
     * "Opportunities over time" — dual-axis line chart: ad opportunity
     * count on the left y-axis, time spent in ad breaks (seconds) on
     * the right. Both series share the same x-axis with the same
     * fillTimeGaps treatment as the other timelines. Counts stay in
     * whole numbers; time renders through formatDurationMs so long
     * windows show "3m 20s" instead of "200".
     */
    renderOpportunityTimeline() {
      const ctx = this.$refs.opportunityTimelineChart
      if (!ctx) return
      this.destroyChart('opportunityTimeline')
      const granularity = this.bucketGranularity()
      const oppFilled  = this.fillTimeGaps(this.opportunityTimelineSeries, 'opportunities', granularity)
      const timeFilled = this.fillTimeGaps(this.opportunityTimelineSeries, 'timeSpentSec', granularity)
      // Tick density and axis title are granularity-driven — a
      // minute-bucketed hour has 60 labels, a day-bucketed month has
      // 30. Same maxTicksLimit for both would either overcrowd the
      // short window or under-label the long one.
      const xTickCap = ({ minute: 12, hour: 12, day: 10, month: 12, year: 10 })[granularity] || 12
      const bucketLabel = ({ minute: 'Per minute', hour: 'Per hour', day: 'Per day', month: 'Per month', year: 'Per year' })[granularity] || ''
      this.charts.opportunityTimeline = new Chart(ctx, {
        type: 'line',
        data: {
          labels: oppFilled.labels,
          datasets: [
            {
              label: 'Ad opportunities',
              data: oppFilled.values,
              borderColor: COLORS.doughnut[0],            // blue
              backgroundColor: 'rgba(51, 147, 255, 0.08)',
              fill: true,
              tension: 0.3,
              pointRadius: 2,
              borderWidth: 2,
              yAxisID: 'y'
            },
            {
              label: 'Time in ad breaks',
              data: timeFilled.values,
              borderColor: COLORS.doughnut[1],            // green
              backgroundColor: 'transparent',
              fill: false,
              tension: 0.3,
              pointRadius: 2,
              borderWidth: 2,
              yAxisID: 'y1'
            }
          ]
        },
        options: {
          responsive: true, maintainAspectRatio: false, animation: false,
          plugins: {
            legend: { position: 'bottom', labels: { font: { family: 'Poppins', size: 12 }, padding: 14, usePointStyle: true, pointStyle: 'circle' } },
            tooltip: {
              callbacks: {
                label: (ctx) => {
                  const v = ctx.parsed.y || 0
                  if (ctx.dataset.yAxisID === 'y1') return `${ctx.dataset.label}: ${this.formatDurationMs(v * 1000)}`
                  return `${ctx.dataset.label}: ${this.formatNumber(v)}`
                }
              }
            }
          },
          scales: {
            ...CHART_SCALES,
            x: {
              ...CHART_SCALES.x,
              title: bucketLabel ? { display: true, text: bucketLabel, color: COLORS.tick, font: { family: 'Poppins', size: 11 } } : { display: false },
              ticks: { ...CHART_SCALES.x.ticks, autoSkip: true, maxTicksLimit: xTickCap, maxRotation: 0 }
            },
            y: {
              ...CHART_SCALES.y,
              beginAtZero: true,
              position: 'left',
              ticks: { ...CHART_SCALES.y.ticks, precision: 0 }
            },
            y1: {
              ...CHART_SCALES.y,
              beginAtZero: true,
              position: 'right',
              grid: { drawOnChartArea: false },      // don't double-draw gridlines
              ticks: { ...CHART_SCALES.y.ticks, callback: (v) => this.formatDurationMs(v * 1000) }
            }
          }
        }
      })
    },

    /**
     * Revenue line chart — gross + earned, two series sharing the
     * x-axis with the other timelines (same fillTimeGaps treatment).
     */
    /**
     * Vue 3 dynamic-ref capture — the template uses `:ref="setRevenueCanvasRef(p.key)"`
     * so each per-pipeline canvas lands in `this._revenueCanvasByKey`
     * under its pipeline key ('ima' | 'ssai'). Named refs on v-for
     * items collapse into arrays otherwise, which loses the mapping.
     */
    setRevenueCanvasRef(key) {
      if (!this._revenueCanvasByKey) this._revenueCanvasByKey = {}
      return (el) => {
        if (el) this._revenueCanvasByKey[key] = el
        else delete this._revenueCanvasByKey[key]
      }
    },

    /**
     * One chart per active pipeline, each with two series: Gross
     * (blue, filled) + Earned (green, line-only). Called from the
     * `revenue` watcher (CPM changes) and from renderAllCharts()
     * (data load, panel switch). Destroys any prior chart instance
     * before rebuilding — Chart.js leaks otherwise. Also destroys
     * charts for pipelines the ad-type filter no longer includes,
     * since the v-for unmounts their canvases.
     */
    renderRevenueTimeline() {
      const granularity = this.bucketGranularity()
      const fmt = (v) => this.formatCurrency(v)
      // Destroy stale pipeline charts (ad-type filter narrowed).
      for (const pipe of ['ima', 'ssai']) {
        if (!this.activePipelines.includes(pipe)) {
          this.destroyChart(`revenueTimeline_${pipe}`)
        }
      }
      for (const key of this.activePipelines) {
        const chartKey = `revenueTimeline_${key}`
        const canvas = this._revenueCanvasByKey?.[key]
        this.destroyChart(chartKey)
        if (!canvas) continue
        const grossField  = key === 'ima' ? 'imaGross'  : 'ssaiGross'
        const grossFilled  = this.fillTimeGaps(this.revenueTimelineSeries, grossField,  granularity)
        // Earned series on hold — reintroduce by uncommenting the
        // second dataset and switching legend `display` back on. The
        // series values are still computed on `revenueTimelineSeries`
        // (imaEarned / ssaiEarned), so no data changes needed.
        // const earnedField = key === 'ima' ? 'imaEarned' : 'ssaiEarned'
        // const earnedFilled = this.fillTimeGaps(this.revenueTimelineSeries, earnedField, granularity)
        this.charts[chartKey] = new Chart(canvas, {
          type: 'line',
          data: {
            labels: grossFilled.labels,
            datasets: [
              {
                label: 'Gross',
                data: grossFilled.values,
                borderColor: COLORS.doughnut[0],            // blue
                backgroundColor: 'rgba(51, 147, 255, 0.08)',
                fill: true,
                tension: 0.3,
                pointRadius: 2,
                borderWidth: 2
              }
              // {
              //   label: 'Earned',
              //   data: earnedFilled.values,
              //   borderColor: COLORS.doughnut[1],            // green
              //   backgroundColor: 'transparent',
              //   fill: false,
              //   tension: 0.3,
              //   pointRadius: 2,
              //   borderWidth: 2
              // }
            ]
          },
          options: {
            responsive: true, maintainAspectRatio: false, animation: false,
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (ctx) => `${ctx.dataset.label}: ${fmt(ctx.parsed.y || 0)}`
                }
              }
            },
            scales: {
              ...CHART_SCALES,
              y: {
                ...CHART_SCALES.y,
                beginAtZero: true,
                ticks: {
                  ...CHART_SCALES.y.ticks,
                  callback: (v) => fmt(v)
                }
              }
            }
          }
        })
      }
    },

    // ---- Panel selector ----
    /** Straight switch — clicking a tab activates it. The old
     * click-active-to-toggle-off behavior made sense when "Off"
     * was a hide-everything state; now that the default tab is
     * "Delivery" (a real view showing ad delivery metrics),
     * toggling it off would leave the user staring at nothing. */
    onAdsPanelPicked(id) {
      if (this.adsPanel === id) return
      this.$store.dispatch('analytics/setAdsPanel', id)
    },

    // ---- Calculator input handlers ----
    onCpmInput() {
      const v = Number(this.cpmInput)
      this.$store.dispatch('analytics/setRevenue', { cpm: Number.isFinite(v) && v >= 0 ? v : 0 })
    },
    onAdDurationSecInput() {
      const v = Number(this.adDurationSecInput)
      // Reject non-positive so the "expected ads = time / dur" formula
      // never divides by zero downstream. 30s default is the industry
      // standard spot length.
      this.$store.dispatch('analytics/setOpportunities', {
        adDurationSec: Number.isFinite(v) && v > 0 ? v : 30
      })
    },
    onOppCpmInput() {
      const v = Number(this.cpmOppInput)
      this.$store.dispatch('analytics/setOpportunities', {
        cpm: Number.isFinite(v) && v >= 0 ? v : 0
      })
    },

    // ---- Revenue calculator helpers ----
    _impressionsToRevenue(impressions, cpm) {
      const n = Number(impressions) || 0
      const c = Number(cpm) || 0
      return (n / 1000) * c
    },

    /**
     * Hint text for the Gross revenue card. Names the actual source
     * per pipeline so the user can tell at a glance whether the
     * number came from impression events (VAST-accurate) or attempts
     * (proxy — used when the impression event never fired). Format:
     *   "1,234 IMA impressions + 567 SSAI attempts × CPM ÷ 1000"
     * When both pipelines used the same source, the label is collapsed.
     */
    _revenueSourceHint() {
      const imaImp  = this.ads?.impressions?.ima  || 0
      const ssaiImp = this.ads?.impressions?.ssai || 0
      const imaAtt  = this.ads?.byPipeline?.ima   || 0
      const ssaiAtt = this.ads?.byPipeline?.ssai  || 0
      const imaN  = imaImp  || imaAtt
      const ssaiN = ssaiImp || ssaiAtt
      const imaSrc  = imaImp  > 0 ? 'impressions' : (imaAtt  > 0 ? 'attempts' : null)
      const ssaiSrc = ssaiImp > 0 ? 'impressions' : (ssaiAtt > 0 ? 'attempts' : null)
      const parts = []
      if (imaN  > 0) parts.push(`${this.formatNumber(imaN)} IMA ${imaSrc}`)
      if (ssaiN > 0) parts.push(`${this.formatNumber(ssaiN)} SSAI ${ssaiSrc}`)
      if (parts.length === 0) return 'No ad activity in window'
      return `${parts.join(' + ')} × CPM ÷ 1000`
    },

    /**
     * "Ads Delivered" count — strictly the VAST impression event
     * total (real + synthesized). No attempts fallback: if a pipeline
     * emits no impressions, its contribution is 0. Honest to the
     * label. The synthesized-impression fallback in YospaceManager
     * (fires ssai_ad_impression after 2s of ad playback when Yospace
     * doesn't emit the real tracking event) is what fills in for SSAI
     * — on the wire those synthesized events are ssai_ad_impression
     * so they're counted here identically to real ones.
     */
    _adsDelivered() {
      return this.ads?.impressions?.total || 0
    },

    /**
     * Prev-period equivalent of _adsDelivered. Same strict rule —
     * impressions only, no attempts fallback — so the delta compares
     * like-to-like.
     */
    _adsDeliveredPrev() {
      return this.ads?.impressions?.totalPrev || 0
    },

    /**
     * Hint text — always labels as "impressions", uniform across
     * pipelines, and shows zero counts too (never falls back to a
     * "no data" message). The underlying source (real VAST impression
     * event vs. the synthesized 2s-playback fallback) is deliberately
     * hidden from the copy: from the user's perspective an impression
     * is an impression.
     */
    _adsDeliveredHint() {
      const total = this._adsDelivered()
      return `${this.formatNumber(total)} impressions`
    },

    /**
     * "Time in ad breaks (per session)" — single-series line of
     * `avgAdMsPerSession` per bucket. Y-axis in seconds for readability;
     * tooltip formats back to durations. Bucket labels go through
     * fillTimeGaps + formatChartLabel so empty buckets show as 0 and
     * the x-axis reads "2 PM" / "May 1" instead of raw "2026-05-01 14:00"
     * (matches the Lives view exactly).
     */
    renderSessionTimeline() {
      const ctx = this.$refs.sessionTimelineChart
      if (!ctx) return
      this.destroyChart('sessionTimeline')
      const series = this.ads?.sessionTimeline || []
      const granularity = this.bucketGranularity()
      const { labels, values } = this.fillTimeGaps(series, 'avgAdMsPerSession', granularity)
      const seconds = values.map(v => (v || 0) / 1000)
      this.charts.sessionTimeline = new Chart(ctx, {
        type: 'line',
        data: {
          labels,
          datasets: [{
            label: 'Avg ad time per session',
            data: seconds,
            borderColor: COLORS.line,
            backgroundColor: COLORS.lineFill,
            fill: true,
            tension: 0.3,
            pointRadius: 2
          }]
        },
        options: {
          responsive: true, maintainAspectRatio: false, animation: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (ctx) => `${this.formatDurationMs((ctx.parsed.y || 0) * 1000)} avg`
              }
            }
          },
          scales: {
            ...CHART_SCALES,
            y: {
              ...CHART_SCALES.y,
              beginAtZero: true,
              ticks: {
                ...CHART_SCALES.y.ticks,
                callback: (v) => this.formatDurationMs(v * 1000)
              }
            }
          }
        }
      })
    },

    /**
     * "Distribution of ad time per session" — horizontal bar over the
     * 5 fixed buckets (0–30s, 30–60s, 1–2m, 2–5m, 5m+). Server emits
     * all five even when empty so the axis stays stable.
     */
    renderHistogram() {
      const ctx = this.$refs.histogramChart
      if (!ctx) return
      this.destroyChart('histogram')
      const buckets = this.ads?.histogram || []
      this.charts.histogram = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: buckets.map(b => b.label),
          datasets: [{
            data: buckets.map(b => b.sessions || 0),
            backgroundColor: COLORS.bar,
            borderRadius: 4
          }]
        },
        options: {
          indexAxis: 'y',
          responsive: true, maintainAspectRatio: false, animation: false,
          plugins: { legend: { display: false } },
          scales: {
            x: { grid: { color: COLORS.grid }, beginAtZero: true, ticks: { font: { family: 'JetBrains Mono', size: 11 }, color: COLORS.tick } },
            y: { grid: { display: false }, ticks: { font: { family: 'Poppins', size: 12 }, color: '#737373' } }
          }
        }
      })
    },

    /**
     * Outcome donut — all five possible states, including in_flight.
     *
     * In-flight = the SDK started the ad clock but a terminal event
     * (complete / skipped / errored / abandoned) never reached the
     * warehouse. Real causes: the ad is currently playing as the
     * dashboard refreshes, the user closed the tab mid-ad on a
     * browser/version that suppresses pagehide too, or the beacon
     * was discarded. Either way, hiding the slice makes the donut
     * silently disagree with the Attempts KPI ("5 attempts" / donut
     * sums to 2), which reads as a bug even when the data is
     * consistent. Show it explicitly (gray slice) so totals
     * reconcile and high in-flight values surface as a signal.
     */
    renderOutcome() {
      const ctx = this.$refs.outcomeChart
      if (!ctx) return
      this.destroyChart('outcome')
      const a = this.ads || {}
      const labels = ['Completed', 'Skipped', 'Abandoned', 'Errored', 'In flight']
      const values = [a.completed || 0, a.skipped || 0, a.abandoned || 0, a.errored || 0, a.inFlight || 0]
      this.charts.outcome = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels,
          datasets: [{ data: values, backgroundColor: COLORS.doughnut.slice(0, labels.length) }]
        },
        options: {
          responsive: true, maintainAspectRatio: false, animation: false, cutout: '65%',
          plugins: {
            legend: { position: 'bottom', labels: { font: { family: 'Poppins', size: 12 }, padding: 14, usePointStyle: true, pointStyle: 'circle' } },
            tooltip: {
              callbacks: {
                label: (ctx) => {
                  const total = values.reduce((s, x) => s + x, 0)
                  const pct = total > 0 ? (ctx.parsed / total * 100).toFixed(1) : 0
                  return `${ctx.label}: ${this.formatNumber(ctx.parsed)} (${pct}%)`
                }
              }
            }
          }
        }
      })
    },

    /**
     * Pipeline donut — IMA preroll vs SSAI midrolls. When the toolbar
     * filters to one pipeline the chart still renders but is
     * effectively single-slice; the donut still reads cleanly.
     */
    renderPipeline() {
      const ctx = this.$refs.pipelineChart
      if (!ctx) return
      this.destroyChart('pipeline')
      const p = this.ads?.byPipeline || {}
      const labels = ['IMA Preroll', 'SSAI Midrolls']
      const values = [p.ima || 0, p.ssai || 0]
      this.charts.pipeline = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels,
          datasets: [{ data: values, backgroundColor: COLORS.doughnut.slice(0, labels.length) }]
        },
        options: {
          responsive: true, maintainAspectRatio: false, animation: false, cutout: '65%',
          plugins: {
            legend: { position: 'bottom', labels: { font: { family: 'Poppins', size: 12 }, padding: 14, usePointStyle: true, pointStyle: 'circle' } },
            tooltip: {
              callbacks: {
                label: (ctx) => `${ctx.label}: ${this.formatNumber(ctx.parsed)}`
              }
            }
          }
        }
      })
    },

    /**
     * "Ad attempts over time" — count of attempts per bucket. Same
     * fillTimeGaps + formatChartLabel treatment as the session
     * timeline, so empty buckets render as 0 and labels match the
     * Lives view's date / time format ("2 PM" / "May 1" / etc.).
     */
    renderAttemptsTimeline() {
      const ctx = this.$refs.attemptsTimelineChart
      if (!ctx) return
      this.destroyChart('attemptsTimeline')
      const series = this.ads?.timeline || []
      const granularity = this.bucketGranularity()
      const { labels, values } = this.fillTimeGaps(series, 'attempts', granularity)
      this.charts.attemptsTimeline = new Chart(ctx, {
        type: 'line',
        data: {
          labels,
          datasets: [{
            label: 'Attempts',
            data: values,
            borderColor: COLORS.line,
            backgroundColor: COLORS.lineFill,
            fill: true,
            tension: 0.3,
            pointRadius: 2
          }]
        },
        options: {
          responsive: true, maintainAspectRatio: false, animation: false,
          plugins: { legend: { display: false } },
          scales: { ...CHART_SCALES, y: { ...CHART_SCALES.y, beginAtZero: true } }
        }
      })
    },

    destroyChart(key) {
      const c = this.charts[key]
      if (c) { c.destroy(); this.charts[key] = null }
    },

    /**
     * Resolve the bucket granularity for chart label formatting.
     * Mirrors the lives.vue helper: use what the API returned if it's
     * there, fall back to the legacy isHourly flag, default to 'day'.
     */
    bucketGranularity() {
      const g = this.data?.viewers?.granularity
      if (g) return g
      return this.data?.viewers?.isHourly === false ? 'day' : 'hour'
    },

    /**
     * Walk the applied date range in bucket-sized steps and project the
     * series' values onto the resulting label set, filling absent
     * buckets with 0. Same shape + semantics as lives.vue's
     * fillTimeGaps so the two views render consistently.
     */
    fillTimeGaps(data, valueKey, granularity) {
      const map = {}
      data.forEach(item => { map[item._id] = item[valueKey] || 0 })

      const startIso = this.applied?.dateFrom ? new Date(this.applied.dateFrom).toISOString() : (data.length && data[0]._id)
      const endIso   = this.applied?.dateTo   ? new Date(this.applied.dateTo).toISOString()   : (data.length && data[data.length - 1]._id)
      if (!startIso || !endIso) return { labels: [], values: [] }

      const start = new Date(this.parseFillStart(startIso, granularity))
      const end   = new Date(this.parseFillStart(endIso, granularity))
      const labels = []
      const values = []

      const step = (d) => {
        if (granularity === 'minute') d.setUTCMinutes(d.getUTCMinutes() + 1)
        if (granularity === 'hour')   d.setUTCHours(d.getUTCHours() + 1)
        if (granularity === 'day')    d.setUTCDate(d.getUTCDate() + 1)
        if (granularity === 'month')  d.setUTCMonth(d.getUTCMonth() + 1)
        if (granularity === 'year')   d.setUTCFullYear(d.getUTCFullYear() + 1)
      }

      const keyFor = (d) => {
        const iso = d.toISOString()
        if (granularity === 'minute') return iso.substring(0, 16).replace('T', ' ')
        if (granularity === 'hour')   return iso.substring(0, 13).replace('T', ' ') + ':00'
        if (granularity === 'day')    return iso.substring(0, 10)
        if (granularity === 'month')  return iso.substring(0, 7)
        return String(d.getUTCFullYear())
      }

      for (let d = new Date(start); d <= end; step(d)) {
        const key = keyFor(d)
        const usesLocal = granularity === 'minute' || granularity === 'hour'
        labels.push(this.formatChartLabel(key, granularity, usesLocal ? d : null))
        values.push(map[key] || 0)
      }
      return { labels, values }
    },

    /** Snap an ISO date to the start of the relevant bucket (UTC). */
    parseFillStart(s, granularity) {
      if (granularity === 'year')  return Date.UTC(parseInt(s.substring(0, 4)), 0, 1)
      if (granularity === 'month') return Date.UTC(parseInt(s.substring(0, 4)), parseInt(s.substring(5, 7)) - 1 || 0, 1)
      if (granularity === 'day')   return new Date(s.substring(0, 10) + 'T00:00:00Z').getTime()
      const d = new Date(s)
      if (granularity === 'hour')   d.setUTCMinutes(0, 0, 0)
      if (granularity === 'minute') d.setUTCSeconds(0, 0)
      return d.getTime()
    },

    /**
     * Humanize a bucket key for the chart x-axis.
     *   minute → "14:23"
     *   hour   → "2 PM"
     *   day    → "May 1"
     *   month  → "May 2026"
     *   year   → "2026"
     */
    formatChartLabel(raw, granularity = 'hour', localDate = null) {
      if (!raw) return raw
      if (granularity === 'minute') {
        if (localDate) {
          const hh = String(localDate.getHours()).padStart(2, '0')
          const mm = String(localDate.getMinutes()).padStart(2, '0')
          return `${hh}:${mm}`
        }
        const m = raw.match(/(\d{2}):(\d{2})$/)
        return m ? `${m[1]}:${m[2]}` : raw
      }
      if (granularity === 'hour') {
        if (localDate) {
          const h = localDate.getHours()
          const ampm = h >= 12 ? 'PM' : 'AM'
          const h12 = h % 12 || 12
          return `${h12} ${ampm}`
        }
        const match = raw.match(/(\d{2}):00$/)
        if (match) {
          const h = parseInt(match[1])
          const ampm = h >= 12 ? 'PM' : 'AM'
          const h12 = h % 12 || 12
          return `${h12} ${ampm}`
        }
        return raw
      }
      if (granularity === 'day') {
        const d = new Date(raw + 'T12:00:00')
        if (isNaN(d)) return raw
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      }
      if (granularity === 'month') {
        const d = new Date(raw + '-01T12:00:00')
        if (isNaN(d)) return raw
        return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
      }
      return raw
    },

    /** Same granularity heuristic as overview/lives so charts read consistently. */
    granularityFor(from, to, presetLabel) {
      const presetMap = {
        'Last Hour':     'minute',
        'Last 6 Hours':  'hour',
        'Today':         'hour',
        'Last 24 Hours': 'hour',
        'Yesterday':     'hour',
        'Last 7 Days':   'day',
        'Last 30 Days':  'day',
        'This Month':    'day',
        'Last Month':    'day',
        'Last Year':     'month'
      }
      if (presetMap[presetLabel]) return presetMap[presetLabel]
      if (!from || !to) return 'hour'
      const ms = new Date(to) - new Date(from)
      const hours = ms / 3600000
      const days  = hours / 24
      if (hours < 1)   return 'minute'
      if (hours < 24)  return 'hour'
      if (days  < 31)  return 'day'
      if (days  < 365) return 'month'
      return 'year'
    },

    formatNumber(n) {
      if (n == null || isNaN(n)) return '—'
      if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M'
      if (n >= 1_000)     return (n / 1_000).toFixed(1) + 'K'
      return Number(n).toLocaleString()
    },

    formatDurationMs(ms) {
      if (ms == null || isNaN(ms)) return '—'
      const totalSeconds = Math.round(ms / 1000)
      const h = Math.floor(totalSeconds / 3600)
      const m = Math.floor((totalSeconds % 3600) / 60)
      const s = totalSeconds % 60
      if (h > 0) return `${h}h ${m}m`
      if (m > 0) return `${m}m ${s}s`
      return `${s}s`
    },

    formatPercent(v) {
      if (v == null || isNaN(v)) return '—'
      return (v * 100).toFixed(1) + '%'
    },

    /**
     * Format a USD amount. Uses Intl.NumberFormat so amounts read with
     * locale grouping ("$1,247.50"); the optional `digits` param lets
     * the "Revenue / session" card show 3 decimals (cents-of-cents
     * matter when a session earns fractions of a dollar).
     */
    formatCurrency(v, digits = 2) {
      if (v == null || isNaN(v)) return '—'
      try {
        return new Intl.NumberFormat('en-US', {
          style: 'currency', currency: 'USD',
          minimumFractionDigits: digits,
          maximumFractionDigits: digits
        }).format(v)
      } catch (_) {
        return '$' + Number(v).toFixed(digits)
      }
    }
  }
}
</script>
